import { useEffect, useState } from "react";
import moment from "moment";

import { Button } from "primereact/button";
import { AutoComplete } from "primereact/autocomplete";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";

import {
  InvoiceService as Service,
  InvoiceService,
} from "../../hoa-don/InvoiceService";

import { useDataTable } from "../../../shared/hooks/DataTable";
import { ToastService } from "../../../AppToast";
import { TenantClientService } from "../../danh-muc/khach-hang/TenantClientService";
import { haveSameInvoiceBuyerNameAcrossSelections } from "../../../utils/invoiceListBuyerValidation";
import { applyInvoiceListColumnFilters } from "../../../utils/invoiceListFilter";
import {
  exceedsInvoiceListSelectionLimit,
  getInvoiceIdentity,
  MAX_INVOICES_PER_INVOICE_LIST,
} from "../../../utils/invoiceListSelectionLimit";

const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: `${index + 1}`,
}));

const getMonthRange = (month, year) => {
  const start = moment()
    .year(year)
    .month(month - 1)
    .startOf("month");

  return {
    fromDate: start.toDate(),
    toDate: start.clone().endOf("month").toDate(),
  };
};

const getDefaultFilters = () => {
  const now = moment();
  const month = now.month() + 1;
  const year = now.year();
  return {
    invoiceSerial: null,
    month,
    year,
    ...getMonthRange(month, year),
    buyerTaxCode: "",
  };
};

const formatDateParam = (value) =>
  value ? moment(value).format("YYYY-MM-DDTHH:mm:ss") : null;

const getInvoiceMonth = (invoice) => {
  const invoiceDate = invoice?.invoiceDate || invoice?.originalInvoiceDate;
  if (!invoiceDate || !moment(invoiceDate).isValid()) {
    return null;
  }
  return moment(invoiceDate).format("YYYY-MM");
};

const haveSameInvoiceMonthAcrossSelections = (
  existingInvoices = [],
  selectedInvoices = [],
) => {
  const invoiceMonths = [...existingInvoices, ...selectedInvoices]
    .map(getInvoiceMonth)
    .filter(Boolean);

  return new Set(invoiceMonths).size <= 1;
};

export const DialogSelectInvoice = (props) => {
  const {
    display,
    setDisplay,
    addSelectedItems,
    selectionMode,
    invoiceStatus,
    sendTaxStatus,
    setDisplayBangKe,
    registerInvoices = [],
    invoiceListMode,
    selectedInvoices = [],
  } = props;

  const [filters, setFilters] = useState(getDefaultFilters());
  const [requiredParams, setRequiredParams] = useState(null);
  const [taxCodeOptions, setTaxCodeOptions] = useState([]);

  const invoiceSerials = (registerInvoices || []).map((item) => ({
    value: item.symbolCode,
    label: item.symbolCode,
  }));

  const validateDateRange = (searchValue) => {
    const { fromDate, toDate } = searchValue;
    if (!fromDate || !toDate) {
      ToastService.error(
        "Vui lòng chọn Từ ngày và Đến ngày trong cùng một tháng.",
      );
      return false;
    }
    if (moment(toDate).isBefore(moment(fromDate), "day")) {
      ToastService.error("Đến ngày không được nhỏ hơn Từ ngày.");
      return false;
    }
    if (!moment(fromDate).isSame(moment(toDate), "month")) {
      ToastService.error("Từ ngày và Đến ngày phải nằm trong cùng một tháng.");
      return false;
    }
    return true;
  };

  const buildSearchParams = (searchValue) => {
    const params: any = {
      invoiceSerial: searchValue.invoiceSerial,
      fromDate: formatDateParam(searchValue.fromDate),
      toDate: formatDateParam(searchValue.toDate),
      buyerTaxCode: searchValue.buyerTaxCode,
    };
    if (invoiceStatus && !invoiceListMode) {
      params.invoiceStatus = invoiceStatus;
    }
    if (sendTaxStatus) {
      params.sendTaxStatus = sendTaxStatus;
    }
    if (invoiceListMode) {
      params.Mode = invoiceListMode;
    }
    return params;
  };

  const search = (searchFilters = filters) => {
    if (!validateDateRange(searchFilters)) {
      return;
    }
    setRequiredParams(buildSearchParams(searchFilters));
  };

  const updateFilter = (field, value) => {
    setFilters((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const searchBuyerTaxCodes = (event) => {
    TenantClientService.getList({
      taxCode: event.query,
      MaxResultCount: 1000,
    }).then(({ items }) => {
      setTaxCodeOptions(items || []);
    });
  };

  const buyerTaxCodeTemplate = (item) => {
    const labels = [item?.taxCode, item?.name, item?.address].filter(Boolean);
    return (
      <div style={{ width: "50rem", whiteSpace: "break-spaces" }}>
        {labels.join(" - ")}
      </div>
    );
  };

  const updateBuyerTaxCode = (value) => {
    updateFilter(
      "buyerTaxCode",
      value && typeof value === "object" ? value.taxCode || "" : value || "",
    );
  };

  const updateMonthYear = (month, year) => {
    const safeMonth = month || moment().month() + 1;
    const safeYear = year || moment().year();
    setFilters((prevState) => ({
      ...prevState,
      month: safeMonth,
      year: safeYear,
      ...getMonthRange(safeMonth, safeYear),
    }));
  };

  const {
    render: renderDataTable,
    items,
    selectedItems,
    setSelectedItems,
    clearItems,
  } = useDataTable({
    header: "",
    dataKey: "id",
    columns: [
      {
        field: "invoiceStatus",
        header: "Trạng thái",
        width: 110,
        matchMode: "contains",
        filterType: "dropdown",
        filterOptions: Service.invoiceStatuses,
        dataType: "custom",
        customCell(rowData: any): any {
          return (
            <span
              className={`invoice-status invoice-status-${rowData.invoiceStatus}`}
            >
              {Service.invoiceStatuses[rowData.invoiceStatus]?.label}
            </span>
          );
        },
      },
      {
        field: "invoiceSerial",
        header: "Ký hiệu",
        width: 100,
        matchMode: "contains",
      },
      {
        field: "invoiceDate",
        header: "Ngày hoá đơn",
        width: 110,
        dataType: "date",
      },
      {
        field: "invoiceNumber",
        header: "Số hoá đơn",
        width: 110,
        matchMode: "contains",
        filterType: "number",
      },
      {
        field: "buyerLegalName",
        header: "Tên đơn vị/Tên người mua",
        minWidth: 250,
        matchMode: "contains",
        dataType: "custom",
        customCell(rowData: any): any {
          return rowData.buyerLegalName || rowData.buyerDisplayName;
        },
      },
      {
        field: "buyerTaxCode",
        header: "Mã số thuế",
        width: 110,
        matchMode: "contains",
      },
      {
        field: "taxAuthorityCode",
        header: "Mã cơ quan thuế",
        width: 150,
        matchMode: "contains",
      },
    ],
    heightCalc: 350,
    filterDelay: 500,
    requiredParams,
    getList: (params, lazyParams) => {
      const requestParams = applyInvoiceListColumnFilters(params, lazyParams);
      if (invoiceListMode) {
        return InvoiceService.getEligibleForInvoiceList(requestParams);
      }
      return InvoiceService.getListInvoiceSign(requestParams);
    },
    selectionMode,
  });

  useEffect(() => {
    if (!display || !items?.length || !selectedInvoices?.length) {
      return;
    }

    const addedInvoiceIds = new Set(
      selectedInvoices.map(getInvoiceIdentity),
    );
    const addedInvoicesOnCurrentPage = items.filter((item) =>
      addedInvoiceIds.has(getInvoiceIdentity(item)),
    );

    if (!addedInvoicesOnCurrentPage.length) {
      return;
    }

    setSelectedItems((currentItems) => {
      const currentSelection = Array.isArray(currentItems) ? currentItems : [];
      const currentInvoiceIds = new Set(
        currentSelection.map(getInvoiceIdentity),
      );
      const missingItems = addedInvoicesOnCurrentPage.filter(
        (item) => !currentInvoiceIds.has(getInvoiceIdentity(item)),
      );

      return missingItems.length
        ? [...currentSelection, ...missingItems]
        : currentItems;
    });
  }, [display, items, selectedInvoices, setSelectedItems]);

  useEffect(() => {
    if (display) {
      const nextFilters = getDefaultFilters();
      setFilters(nextFilters);
      setRequiredParams(null);
      setTaxCodeOptions([]);
      clearItems();
    }
  }, [display, registerInvoices]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    if (!selectedItems || selectedItems.length === 0) {
      ToastService.error("Vui lòng chọn ít nhất một hóa đơn.");
      return;
    }
    if (
      exceedsInvoiceListSelectionLimit(selectedInvoices, selectedItems)
    ) {
      ToastService.error(
        `Mỗi bảng kê chỉ được chọn tối đa ${MAX_INVOICES_PER_INVOICE_LIST.toLocaleString(
          "vi-VN",
        )} hóa đơn.`,
      );
      return;
    }
    if (
      !haveSameInvoiceMonthAcrossSelections(
        selectedInvoices,
        selectedItems,
      )
    ) {
      ToastService.error(
        "Các hóa đơn được chọn phải nằm trong cùng một tháng.",
      );
      return;
    }
    if (
      !haveSameInvoiceBuyerNameAcrossSelections(
        selectedInvoices,
        selectedItems,
      )
    ) {
      ToastService.error(
        "Các hóa đơn được chọn phải có cùng Tên đơn vị/Tên người mua.",
      );
      return;
    }
    const handled = addSelectedItems(selectedItems, filters);
    if (handled === false) {
      return;
    }
    setDisplay(false);
    setDisplayBangKe?.(true);
  };

  return (
    <Dialog
      header={invoiceListMode ? "Chọn hóa đơn vào bảng kê" : "Chọn hoá đơn"}
      visible={display}
      style={{ width: "92rem" }}
      draggable={false}
      resizable={true}
      position="top"
      onHide={() => setDisplay(false)}
    >
      <div className="flex align-items-center gap-3 flex-wrap mb-3">
        <div className="flex align-items-center gap-2">
          <label>Ký hiệu</label>
          <Dropdown
            value={filters.invoiceSerial}
            options={invoiceSerials}
            filter
            showClear
            style={{ width: "11rem" }}
            onChange={(e) => updateFilter("invoiceSerial", e.value)}
          />
        </div>
        <div className="flex align-items-center gap-2">
          <label>Tháng</label>
          <Dropdown
            value={filters.month}
            options={monthOptions}
            style={{ width: "6rem" }}
            onChange={(e) => updateMonthYear(e.value, filters.year)}
          />
        </div>
        <div className="flex align-items-center gap-2">
          <label>Năm</label>
          <InputNumber
            value={filters.year}
            useGrouping={false}
            inputStyle={{ width: "5rem" }}
            onValueChange={(e) => updateMonthYear(filters.month, e.value)}
          />
        </div>
        <div className="flex align-items-center gap-2">
          <label>Từ ngày</label>
          <Calendar
            value={filters.fromDate}
            dateFormat="dd/mm/yy"
            style={{ width: "9rem" }}
            onChange={(e) => updateFilter("fromDate", e.value)}
          />
        </div>
        <div className="flex align-items-center gap-2">
          <label>Đến ngày</label>
          <Calendar
            value={filters.toDate}
            dateFormat="dd/mm/yy"
            style={{ width: "9rem" }}
            onChange={(e) => updateFilter("toDate", e.value)}
          />
        </div>
        <div className="flex align-items-center gap-2">
          <label>Mã số thuế</label>
          <AutoComplete
            value={filters.buyerTaxCode}
            suggestions={taxCodeOptions}
            completeMethod={searchBuyerTaxCodes}
            field="taxCode"
            itemTemplate={buyerTaxCodeTemplate}
            style={{ width: "13rem" }}
            onChange={(e) => updateBuyerTaxCode(e.value)}
          />
        </div>
        <Button
          icon="pi pi-search"
          className="p-button-rounded p-button-outlined ml-auto"
          onClick={() => search()}
        />
      </div>

      <div>{renderDataTable()}</div>

      <div className="flex justify-content-end">
        <Button
          label="Đóng"
          icon="pi pi-times"
          onClick={() => setDisplay(false)}
          className="p-button-text p-button-sm"
        />
        <Button
          label="Tạo bảng kê"
          icon="pi pi-check"
          onClick={handleSubmit}
          className="p-button-sm"
        />
      </div>
    </Dialog>
  );
};
